import axios, { AxiosInstance, AxiosError } from 'axios'
import { useAuthStore } from '@stores/authStore'

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://fmcg-ops-api.xoraindia.com/api/v1'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const { token } = useAuthStore.getState()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          try {
            const { refreshToken } = useAuthStore.getState()
            if (refreshToken) {
              const response = await axios.post(
                `${API_BASE_URL}/auth/refresh`,
                { refreshToken }
              )

              const { accessToken, refreshToken: newRefreshToken, user } = response.data.data
              useAuthStore.getState().setAuth(accessToken, newRefreshToken, user)

              originalRequest.headers.Authorization = `Bearer ${accessToken}`
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            useAuthStore.getState().logout()
            window.location.href = '/login'
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.client.post('/auth/login', { email, password })
    // Handle both response formats
    const data = response.data.data || response.data
    return {
      accessToken: data.token || data.accessToken,
      refreshToken: data.refreshToken || 'default-refresh-token',
      user: data.user || { id: data.id, email: data.email, name: data.name, role: data.role }
    }
  }

  async signup(data: { companyName: string; fullName: string; email: string; password: string; phone?: string; loadDemoData?: boolean }) {
    const response = await this.client.post('/auth/signup', data)
    const d = response.data.data || response.data
    return { accessToken: d.accessToken, refreshToken: d.refreshToken, user: d.user }
  }

  async refreshToken(refreshToken: string) {
    const response = await this.client.post('/auth/refresh', { refreshToken })
    return response.data.data
  }

  async getCurrentUser() {
    const response = await this.client.get('/auth/me')
    return response.data.data
  }

  // Generic GET request
  async get<T>(url: string, config = {}) {
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  // Generic POST request
  async post<T>(url: string, data?: any, config = {}) {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  // Generic PUT request
  async put<T>(url: string, data?: any, config = {}) {
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  // Generic DELETE request
  async delete<T>(url: string, config = {}) {
    const response = await this.client.delete<T>(url, config)
    return response.data
  }

  // Generic PATCH request
  async patch<T>(url: string, data?: any, config = {}) {
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }
}

export const apiClient = new ApiClient()
